import classNames from "classnames";
import React from "react";
import useElementOnScreen from "../../../hooks/useElementOnScreen";
import "./Typography.scss";

type TextTag =
	| "p"
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "span"
	| "div"
	| "label"
	| "legend"
	| "figcaption";

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

type TypographyProps = {
	children: React.ReactNode;
	className?: string;
	as?: TextTag;
	ref?: React.Ref<HTMLElement>;
	[key: string]: unknown;
};

export const Typography: React.FC<TypographyProps> = ({
	children,
	as = "p",
	className,
	ref,
	...props
}) => {
	const [containerRef, isVisible] = useElementOnScreen<Element>();
	const Tag = as;

	const hasFadeInFromDirection = /fade-in--from-[^\s]+/.test(className || "");

	return (
		<Tag
			className={classNames(
				"primary-font typography fade-in",
				className || "",
				{
					"fade-in--from-left": !hasFadeInFromDirection,
					visible: isVisible,
				},
			)}
			{...props}
			ref={containerRef as any}>
			{children}
		</Tag>
	);
};

type HeadingProps = {
	children: React.ReactNode;
	className?: string;
	as?: HeadingTag;
	ref?: React.Ref<HTMLElement>;
	[key: string]: unknown;
};

const sizesMap: Record<HeadingTag, string> = {
	h1: "text-xxl",
	h2: "text-xl",
	h3: "text-lg",
	h4: "text-md",
	h5: "text-md",
	h6: "text-sm",
};

export const Heading = ({
	children,
	className,
	as = "h2",
	...props
}: HeadingProps) => {
	const classNameString = classNames(`font-regular ${sizesMap[as]}`, className);

	return (
		<Typography as={as} className={classNameString} {...props}>
			{children}
		</Typography>
	);
};

export default Typography;
